import {
  createContext,
  FC,
  ReactNode,
  useEffect,
  useState,
  useMemo,
} from 'react';
import { useLocation } from 'react-router-dom';

import { useChannel } from 'ably/react';
import { differenceInMinutes } from 'date-fns';

import { CfsApi, UnitApi } from '@/api';
import { IcMarkerCfs } from '@/assets/images';
import { IMarker, UnitShiftMarkerInfoItem } from '@/components/GoogleMap';
import {
  ASSIGNMENT_STATUS,
  LocalStorageItems,
  UnitShiftMarkerColors,
  UnitShiftMarkerIcons,
} from '@/constants';
import {
  AssignedUnitShiftStatus,
  IAddress,
  IAssignedUnitShift,
  ICFS,
  IUnitShift,
  IUnitShiftLocation,
  UnitShiftStatus,
  FilterValues,
  IUnit,
  AssignmentStatus,
  Role,
} from '@/models';
import {
  getAddressInfoCoordinates,
  getAddressInfoLocation,
  getAddressLocation,
} from '@/services';
import { useAgencyStore, useToastStore, useUserStore } from '@/store';
import { colors } from '@/theme/variables';
import { openNewWindow } from '@/utils';

export interface AssignedUnitShiftItem extends IAssignedUnitShift {
  cfsId?: string;
  cfsNumber?: string;
  cfsDescription?: string;
  searchText?: string;
}

interface CFSFilterState {
  severity: string;
  assignmentStatus: string;
  agency?: string;
  readBy: boolean;
  searchText: string;
  agencyType?: string;
}

interface UnitShiftContextValue {
  onDutyUnitShifts: IUnitShift[];
  waitingUnitShifts: IUnitShift[];
  scheduledUnitShifts: IUnitShift[];
  assignedUnitShifts: AssignedUnitShiftItem[];
  unitShiftMarkers: IMarker[];
  filteredUnitShiftMarkers: IMarker[];
  activeCfsMarkers: IMarker[];
  unitShiftAddresses: UnitShiftAddresses;
  filterValues: FilterValues;
  searchText: string;
  setFilterValues: (values: FilterValues) => void;
  setSearchText: (text: string) => void;
  fetchScheduledUnitShifts: () => Promise<IUnitShift[]>;
  fetchOnDutyUnitShifts: () => Promise<IUnitShift[]>;
  fetchWaitingUnitShifts: () => Promise<IUnitShift[]>;
  fetchAssignedUnitShifts: () => Promise<void>;
  fetchAllActiveUnitShifts: () => Promise<{
    active: IUnitShift[];
    waiting: IUnitShift[];
    scheduled: IUnitShift[];
  }>;
  cfsSearchText: string;
  setCfsSearchText: (text: string) => void;
  cfsFilterState: CFSFilterState;
  setCfsFilterState: (filter: Partial<CFSFilterState>) => void;
  filteredCfsMarkers: IMarker[];
  allFetchedUnitsMap: Record<string, IUnit>;
  setBackRoute?: (route: string) => void;
  clearBackRoute?: () => void;
  backRoute?: string;
}

interface UnitShiftProviderProps {
  children: ReactNode;
  initialCFSList?: ICFS[];
}

interface IUnitShiftAddress {
  unitShift: string;
  address: IAddress;
}

type UnitShiftAddresses = Record<string, string>;

const initFilterValues: FilterValues = {
  agency: null,
  beat: null,
  agencyType: null,
  unitTypes: null,
};

export const UnitShiftContext = createContext<UnitShiftContextValue>({
  onDutyUnitShifts: [],
  scheduledUnitShifts: [],
  waitingUnitShifts: [],
  assignedUnitShifts: [],
  unitShiftMarkers: [],
  filteredUnitShiftMarkers: [],
  activeCfsMarkers: [],
  unitShiftAddresses: {},
  filterValues: initFilterValues,
  searchText: '',
  setSearchText: () => {},
  setFilterValues: () => {},
  fetchScheduledUnitShifts: async () => [],
  fetchOnDutyUnitShifts: async () => [],
  fetchWaitingUnitShifts: async () => [],
  fetchAssignedUnitShifts: async () => {},
  fetchAllActiveUnitShifts: async () => ({
    active: [],
    waiting: [],
    scheduled: [],
  }),
  cfsSearchText: '',
  setCfsSearchText: () => {},
  cfsFilterState: {
    severity: '',
    assignmentStatus: '',
    readBy: false,
    searchText: '',
  },
  setCfsFilterState: () => {},
  filteredCfsMarkers: [],
  allFetchedUnitsMap: {},
  setBackRoute: () => {},
  clearBackRoute: () => {},
  backRoute: undefined,
});

export const UnitShiftProvider: FC<UnitShiftProviderProps> = (props) => {
  const { children, initialCFSList } = props;
  const { updateToast } = useToastStore();
  const { account, user } = useUserStore();
  const { agencies } = useAgencyStore();
  const currentLocation = useLocation();
  const [assignedUnitShifts, setAssignedUnitShifts] = useState<
    AssignedUnitShiftItem[]
  >([]);
  const [allAssignedUnitShifts, setAllAssignedUnitShifts] = useState<
    IUnitShift[]
  >([]);
  const [scheduledUnitShifts, setScheduledUnitShifts] = useState<IUnitShift[]>(
    [],
  );
  const [onDutyUnitShifts, setOnDutyUnitShifts] = useState<IUnitShift[]>([]);
  const [waitingUnitShifts, setWaitingUnitShifts] = useState<IUnitShift[]>([]);
  const [unitShiftMarkers, setUnitShiftMarkers] = useState<IMarker[]>([]);
  const [activeCfsMarkers, setActiveCfsMarkers] = useState<IMarker[]>([]);
  const [unitShiftAddresses, setUnitShiftAddresses] =
    useState<UnitShiftAddresses>({});
  const [filterValues, setFilterValues] =
    useState<FilterValues>(initFilterValues);
  const [searchText, setSearchText] = useState('');
  const [cfsSearchText, setCfsSearchText] = useState('');
  const [cfsFilterState, setCfsFilterState] = useState<CFSFilterState>({
    severity: '',
    assignmentStatus: '',
    readBy: false,
    searchText: '',
  });
  const [cfsList, setCfsList] = useState<ICFS[]>([]);
  const [allFetchedUnitsMap, setAllFetchedUnitsMap] = useState<
    Record<string, IUnit>
  >({});

  const [backRoute, setBackRoute] = useState<string | undefined>(() => {
    // Initialize from localStorage if available
    const stored = localStorage.getItem('cfs-back-route');
    return stored && stored !== 'undefined' ? stored : undefined;
  });

  // Enhanced setBackRoute function
  const handleSetBackRoute = (route: string) => {
    setBackRoute(route);
    // Persist to localStorage for cross-page navigation
    localStorage.setItem('cfs-back-route', route);
  };

  const clearBackRoute = () => {
    setBackRoute(undefined);
    localStorage.removeItem('cfs-back-route');
  };

  const isUnitMatchingFilters = (unit: IUnit) => {
    // Search filter
    if (
      searchText &&
      !unit.name.toLowerCase().includes(searchText.toLowerCase())
    ) {
      return false;
    }

    // Agency filter
    if (filterValues.agency && filterValues.agency !== unit.agency) {
      return false;
    }

    // Agency type filter
    if (
      filterValues.agencyType &&
      unit.type.agencyType !== filterValues.agencyType
    ) {
      return false;
    }

    // Unit type filter
    if (
      filterValues.unitTypes?.length &&
      !filterValues.unitTypes.includes(unit.type._id as string)
    ) {
      return false;
    }

    // Beat filter
    if (filterValues.beat && !unit.polygons?.includes(filterValues.beat)) {
      return false;
    }

    return true;
  };

  const getUnitShiftMarker = (
    unitShift: IUnitShift,
    assignedStatus?: AssignedUnitShiftStatus,
    cfsNumber?: string,
    cfsDescription?: string,
  ) => {
    if (unitShift?.location && unitShift?.status !== UnitShiftStatus.CLOSED) {
      const isOffline =
        unitShift?.location?.timestamp &&
        differenceInMinutes(
          new Date(),
          new Date(unitShift?.location?.timestamp),
        ) > 5;

      const IconSvg = UnitShiftMarkerIcons[unitShift.unit.type.agencyType];
      const iconColor =
        assignedStatus !== AssignedUnitShiftStatus.ASSIGNED &&
        assignedStatus !== AssignedUnitShiftStatus.EN_ROUTE
          ? UnitShiftMarkerColors[unitShift.status]
          : UnitShiftMarkerColors[assignedStatus];
      return {
        key: String(unitShift._id),
        title: unitShift.unit.name,
        position: {
          lng: unitShift.location?.point?.coordinates[0],
          lat: unitShift.location?.point?.coordinates[1],
        },
        info: [
          <UnitShiftMarkerInfoItem
            key={unitShift.unit._id + true}
            unitName={unitShift.unit.name}
            unitEmployees={unitShift.users}
            cfsNumber={cfsNumber}
            cfsDescription={cfsDescription}
            vehicleName={unitShift.vehicles?.[0]?.name}
            timestamp={unitShift.location.timestamp}
            address={unitShift.location.address}
            isOffline={true}
          />,
          <UnitShiftMarkerInfoItem
            key={unitShift.unit._id + false}
            unitName={unitShift.unit.name}
            unitEmployees={unitShift.users}
            cfsNumber={cfsNumber}
            cfsDescription={cfsDescription}
            vehicleName={unitShift.vehicles?.[0]?.name}
            timestamp={unitShift.location.timestamp}
            address={unitShift.location.address}
            isOffline={false}
          />,
        ],
        icon: isOffline ? (
          <div className="relative bg-red-500/40 h-14 w-14 rounded-full ring-1 ring-red-500/80">
            <IconSvg
              className="absolute top-[14px] left-[10px]"
              {...iconColor}
            />
          </div>
        ) : (
          <IconSvg {...iconColor} />
        ),
      };
    } else return null;
  };

  const filteredUnitShiftMarkers = useMemo(() => {
    return unitShiftMarkers.filter((marker) => {
      const unitShift = [
        ...onDutyUnitShifts,
        ...assignedUnitShifts.map((aus) => aus.unitShift),
      ].find((us) => String(us?._id) === marker.key);

      if (!unitShift) return true;

      return isUnitMatchingFilters(unitShift?.unit);
    });
  }, [
    unitShiftMarkers,
    filterValues,
    searchText,
    onDutyUnitShifts,
    assignedUnitShifts,
  ]);

  // Generate markers with memoization to avoid unnecessary recalculations
  const computeUnitShiftMarkers = useMemo(() => {
    // Filter assigned units
    const filteredAssignedUnits = assignedUnitShifts.filter(
      (assignedUnitShift) => {
        const unitShift = assignedUnitShift.unitShift;

        if (unitShift?.status !== UnitShiftStatus.ON_CALL) return false;

        if (
          assignedUnitShift.status === AssignedUnitShiftStatus.COMPLETED ||
          assignedUnitShift.status === AssignedUnitShiftStatus.CANCELED
        ) {
          return false;
        }
        return isUnitMatchingFilters(unitShift.unit);
      },
    );

    // Filter on-duty units
    const filteredOnDutyUnits = onDutyUnitShifts.filter(
      (unitShift) =>
        unitShift.status === UnitShiftStatus.ACTIVE &&
        isUnitMatchingFilters(unitShift.unit),
    );

    const markers: IMarker[] = [];
    const seenKeys = new Set<string>();

    // Create markers for filtered assigned units
    filteredAssignedUnits.forEach(
      ({ unitShift, status, cfsDescription, cfsNumber }) => {
        const marker = getUnitShiftMarker(
          unitShift,
          status,
          cfsNumber,
          cfsDescription,
        );
        if (marker && !seenKeys.has(marker.key)) {
          seenKeys.add(marker.key);
          markers.push(marker);
        }
      },
    );

    // Create markers for filtered on-duty units
    filteredOnDutyUnits.forEach((unitShift) => {
      const marker = getUnitShiftMarker(unitShift);
      if (marker && !seenKeys.has(marker.key)) {
        seenKeys.add(marker.key);
        markers.push(marker);
      }
    });

    return markers;
  }, [onDutyUnitShifts, assignedUnitShifts, filterValues, searchText]);

  // Update unitShiftMarkers when computed markers change
  useEffect(() => {
    setUnitShiftMarkers(computeUnitShiftMarkers);
  }, [computeUnitShiftMarkers]);

  useChannel(`account:${account?._id}:unit-shift`, (message) => {
    if (message.name === 'UPDATED_LOCATION') {
      const unitShiftId = message?.extras?.headers?.unitShift;
      if (message?.data.location && unitShiftId) {
        updateUnitShiftLocation(unitShiftId, message?.data.location);
      }
    }
  });

  useChannel(`account:${account?._id}:unit-shift`, ({ data, extras, name }) => {
    const unitShiftId = extras?.headers?.unitShift;
    if (data.status) {
      if (data.status === UnitShiftStatus.ACTIVE) {
        fetchOnDutyUnitShifts();
      } else if (data.status === UnitShiftStatus.WAITING) {
        fetchWaitingUnitShifts();
      } else if (data.status === UnitShiftStatus.SCHEDULED) {
        fetchScheduledUnitShifts();
      } else {
        const newScheduledUnitShifts = scheduledUnitShifts.filter(
          ({ _id }) => _id !== unitShiftId,
        );
        if (newScheduledUnitShifts.length !== scheduledUnitShifts.length) {
          setScheduledUnitShifts(newScheduledUnitShifts);
        } else {
          const newOnDutyUnitShifts = onDutyUnitShifts.filter(
            ({ _id }) => _id !== unitShiftId,
          );
          if (newOnDutyUnitShifts.length !== onDutyUnitShifts.length) {
            setOnDutyUnitShifts(newOnDutyUnitShifts);
          } else {
            const newWaitingUnitShifts = waitingUnitShifts.filter(
              ({ _id }) => _id !== unitShiftId,
            );
            setWaitingUnitShifts(newWaitingUnitShifts);
          }
        }
      }
    } else if (name === 'UPDATED' && unitShiftId) {
      if (onDutyUnitShifts.some(({ _id }) => _id === unitShiftId)) {
        fetchOnDutyUnitShifts();
      } else if (waitingUnitShifts.some(({ _id }) => _id === unitShiftId)) {
        fetchWaitingUnitShifts();
      } else if (scheduledUnitShifts.some(({ _id }) => _id === unitShiftId)) {
        fetchScheduledUnitShifts();
      }
    }
  });

  useChannel(
    `account:${account?._id}:cfs:assigned-unit-shift`,
    async (message) => {
      const cfsId = message?.extras?.headers?.cfs;

      if (!cfsId) {
        fetchAssignedUnitShifts();
        return;
      }

      const newCFS = await fetchCFS(cfsId);
      if (!newCFS) {
        fetchAssignedUnitShifts();
        return;
      }

      const { _assignedUnitShifts, _cfsMarkers } =
        extractAssignedUnitShifts(newCFS);

      setAssignedUnitShifts((prev) => {
        const remaining = prev.filter((shift) => shift.cfsId !== cfsId);
        return remaining.concat(_assignedUnitShifts);
      });
      setActiveCfsMarkers((prev) => [
        ...prev.filter((marker) => marker.key !== cfsId),
        ..._cfsMarkers,
      ]);
    },
  );

  useChannel(`account:${account?._id}:unit-shift:address`, async (message) => {
    const { data } = message;
    if (data.unitShiftAddresses) {
      const addressObj = (
        data.unitShiftAddresses as IUnitShiftAddress[]
      ).reduce((obj: UnitShiftAddresses, current) => {
        obj[current.unitShift] = getAddressLocation(current.address);
        return obj;
      }, {});
      setUnitShiftAddresses(addressObj);
    }
  });

  useEffect(() => {
    if (initialCFSList === undefined) {
      fetchAssignedUnitShifts();
    }
  }, []);

  useEffect(() => {
    if (!initialCFSList?.length) return;

    let _assignedUnitShifts: AssignedUnitShiftItem[] = [];
    let _assignedUnitShiftsList: IUnitShift[] = [];
    let _cfsMarkers: IMarker[] = [];

    initialCFSList.forEach((cfs) => {
      const {
        _assignedUnitShifts: newAUS,
        _cfsMarkers: newMarkers,
        _assignedUnitShiftsList: newAUSList,
      } = extractAssignedUnitShifts(cfs);
      _assignedUnitShifts = _assignedUnitShifts.concat(newAUS);
      _assignedUnitShiftsList = _assignedUnitShiftsList.concat(newAUSList);
      _cfsMarkers = _cfsMarkers.concat(newMarkers);
    });

    setCfsList(initialCFSList);
    setAllAssignedUnitShifts(_assignedUnitShiftsList);
    setAssignedUnitShifts(_assignedUnitShifts);
    setActiveCfsMarkers(_cfsMarkers);
  }, [initialCFSList]); // eslint-disable-line react-hooks/exhaustive-deps

  const extractAssignedUnitShifts = (_cfs: ICFS) => {
    const _assignedUnitShifts: AssignedUnitShiftItem[] = [];
    const _assignedUnitShiftsList: IUnitShift[] = [];
    const _cfsMarkers: IMarker[] = [];
    _cfs.assignedUnitShifts.forEach((assignedUnitShift) => {
      _assignedUnitShifts.push({
        ...assignedUnitShift,
        cfsId: _cfs._id,
        cfsNumber: _cfs.number,
        cfsDescription: _cfs.shortDescription,
      });
      _assignedUnitShiftsList.push(assignedUnitShift.unitShift);
    });
    const coordinates = getAddressInfoCoordinates(_cfs?.addressInfo);
    const addressString = getAddressInfoLocation(_cfs?.addressInfo);

    if (coordinates) {
      _cfsMarkers.push({
        key: String(_cfs._id),
        title: _cfs.number,
        position: {
          lng: coordinates.lng,
          lat: coordinates.lat,
        },
        address: addressString,
        icon: (
          <IcMarkerCfs
            color={
              _cfs.isReadyToDispatch &&
              _cfs.assignmentStatus === AssignmentStatus.NEW
                ? colors.warning.main
                : ASSIGNMENT_STATUS[_cfs.assignmentStatus].color
            }
          />
        ),
        onClick: () => {
          const displayOption =
            localStorage.getItem('cfs-display-option') || 'slide-view';

          // Set the back route based on current location
          const currentPath = currentLocation.pathname;

          // Set the back route based on current location
          if (
            currentPath.includes('/map-view') ||
            currentPath.includes('/my-cfs')
          ) {
            handleSetBackRoute(currentPath);
          } else if (currentPath.includes('/cfs-active')) {
            handleSetBackRoute('/cfs-active');
          } else if (
            currentPath.includes('/cfs') &&
            !currentPath.includes('/cfs/')
          ) {
            handleSetBackRoute('/cfs');
          } else {
            // Default fallback for other pages
            handleSetBackRoute(currentPath);
          }

          if (displayOption === 'new-window') {
            openNewWindow(`/cfs/${_cfs._id}`, _cfs._id);
          } else {
            // Use window.location.href for more reliable navigation from map context
            window.location.href = `/cfs/${_cfs._id}`;
          }
        },
      });
    }
    return { _assignedUnitShifts, _cfsMarkers, _assignedUnitShiftsList };
  };

  const fetchCFS = async (cfsId: string) => {
    try {
      const res = await CfsApi.getOne(cfsId);
      return res.data;
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
      return null;
    }
  };

  const updateUnitShiftLocation = (
    unitShiftId: string,
    location: IUnitShiftLocation,
  ) => {
    // Check assigned unit shifts first
    const assignedShiftIndex = assignedUnitShifts.findIndex(
      (shift) => shift.unitShift._id === unitShiftId,
    );

    if (assignedShiftIndex >= 0) {
      // Update in assigned shifts
      setAssignedUnitShifts((prevShifts) => {
        const newShifts = [...prevShifts];
        newShifts[assignedShiftIndex] = {
          ...newShifts[assignedShiftIndex],
          unitShift: {
            ...newShifts[assignedShiftIndex].unitShift,
            location: { ...location },
          },
        };
        return newShifts;
      });
      return; // Exit early since we found and updated the shift
    }

    // If not found in assigned shifts, check on-duty shifts
    const onDutyShiftIndex = onDutyUnitShifts.findIndex(
      (shift) => shift._id === unitShiftId,
    );

    if (onDutyShiftIndex >= 0) {
      // Update in on-duty shifts
      setOnDutyUnitShifts((prevShifts) => {
        const newShifts = [...prevShifts];
        newShifts[onDutyShiftIndex] = {
          ...newShifts[onDutyShiftIndex],
          location: { ...location },
        };
        return newShifts;
      });
    }
  };

  const getAllFetchedUnitsMap = useMemo(() => {
    const allUnits = [
      ...allAssignedUnitShifts,
      ...onDutyUnitShifts,
      ...waitingUnitShifts,
      ...scheduledUnitShifts,
    ].map((unitShift) => unitShift?.unit);

    return allUnits.reduce((map, unit) => {
      if (unit?._id) {
        map[unit?._id] = unit;
      }
      return map;
    }, {} as Record<string, IUnit>);
  }, [
    onDutyUnitShifts,
    waitingUnitShifts,
    scheduledUnitShifts,
    allAssignedUnitShifts,
  ]);

  useEffect(() => {
    setAllFetchedUnitsMap(getAllFetchedUnitsMap);
  }, [getAllFetchedUnitsMap]);

  const getCurrentAgencyId = () => {
    return localStorage.getItem(LocalStorageItems.CurrentAgencyId) || null;
  };
  const getAgencyIdFromSearch = () => {
    return new URLSearchParams(window.location.search).get('agencyId');
  };

  // Helper function to check if user has dispatcher role
  const hasDispatcherRole = useMemo(() => {
    return !!user?.roles?.some((role) => role.role.includes(Role.DISPATCHER));
  }, [user?.roles]);

  const fetchScheduledUnitShifts = async () => {
    try {
      const filter = JSON.stringify({
        status: UnitShiftStatus.SCHEDULED,
      });
      const res = await UnitApi.getUnitShifts({ filter, limit: 1000 });
      const agencyIdFromSearch = getAgencyIdFromSearch();
      const agencyIdForFilter =
        agencyIdFromSearch ||
        (!hasDispatcherRole ? getCurrentAgencyId() : null);
      if (agencyIdForFilter) {
        const filteredScheduledUnitShifts = res.data.results.filter(
          (unitShift) => unitShift.unit.agency === agencyIdForFilter,
        );
        setScheduledUnitShifts(filteredScheduledUnitShifts);
        return filteredScheduledUnitShifts;
      } else {
        setScheduledUnitShifts(res.data.results);
        return res.data.results;
      }
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
      return [];
    }
  };

  const fetchOnDutyUnitShifts = async () => {
    try {
      const filter = JSON.stringify({
        status: 'ACTIVE',
      });
      const res = await UnitApi.getUnitShifts({ filter, limit: 1000 });
      const agencyIdFromSearch = getAgencyIdFromSearch();
      const agencyIdForFilter =
        agencyIdFromSearch ||
        (!hasDispatcherRole ? getCurrentAgencyId() : null);
      if (agencyIdForFilter) {
        const filteredOnDutyUnitShifts = res.data.results.filter(
          (unitShift) => unitShift.unit.agency === agencyIdForFilter,
        );
        setOnDutyUnitShifts(filteredOnDutyUnitShifts);
        return filteredOnDutyUnitShifts;
      } else {
        setOnDutyUnitShifts(res.data.results);
        return res.data.results;
      }
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
      return [];
    }
  };

  const fetchWaitingUnitShifts = async () => {
    try {
      const filter = JSON.stringify({
        status: 'WAITING',
      });
      const res = await UnitApi.getUnitShifts({ filter, limit: 1000 });
      const agencyIdFromSearch = getAgencyIdFromSearch();
      const agencyIdForFilter =
        agencyIdFromSearch ||
        (!hasDispatcherRole ? getCurrentAgencyId() : null);
      if (agencyIdForFilter) {
        const filteredWaitingUnitShifts = res.data.results.filter(
          (unitShift) => unitShift.unit.agency === agencyIdForFilter,
        );
        setWaitingUnitShifts(filteredWaitingUnitShifts);
        return filteredWaitingUnitShifts;
      } else {
        setWaitingUnitShifts(res.data.results);
        return res.data.results;
      }
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
      return [];
    }
  };

  const fetchAllActiveUnitShifts = async () => {
    try {
      const filter = JSON.stringify({
        status: {
          $in: [
            UnitShiftStatus.ACTIVE,
            UnitShiftStatus.WAITING,
            UnitShiftStatus.SCHEDULED,
          ],
        },
      });
      const res = await UnitApi.getUnitShifts({ filter, limit: 1000 });
      const agencyIdFromSearch = getAgencyIdFromSearch();
      const agencyIdForFilter =
        agencyIdFromSearch ||
        (!hasDispatcherRole ? getCurrentAgencyId() : null);

      const all = agencyIdForFilter
        ? res.data.results.filter(
            (unitShift) => unitShift.unit.agency === agencyIdForFilter,
          )
        : res.data.results;

      const active = all.filter((s) => s.status === UnitShiftStatus.ACTIVE);
      const waiting = all.filter((s) => s.status === UnitShiftStatus.WAITING);
      const scheduled = all.filter(
        (s) => s.status === UnitShiftStatus.SCHEDULED,
      );

      setOnDutyUnitShifts(active);
      setWaitingUnitShifts(waiting);
      setScheduledUnitShifts(scheduled);

      return { active, waiting, scheduled };
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
      return { active: [], waiting: [], scheduled: [] };
    }
  };

  const fetchAssignedUnitShifts = async () => {
    try {
      let _assignedUnitShifts: AssignedUnitShiftItem[] = [];
      let _assignedUnitShiftsList: IUnitShift[] = [];
      let _cfsMarkers: IMarker[] = [];
      const res = await CfsApi.activeList({
        sort: JSON.stringify({ createdAt: -1 }),
      });
      setCfsList(res.data);
      res.data.forEach((cfs) => {
        const {
          _assignedUnitShifts: newAssignedUnitShifts,
          _cfsMarkers: newCfsMarkers,
          _assignedUnitShiftsList: newAssignedUnitShiftsList,
        } = extractAssignedUnitShifts(cfs);
        _assignedUnitShifts = _assignedUnitShifts.concat(newAssignedUnitShifts);
        _assignedUnitShiftsList = _assignedUnitShiftsList.concat(
          newAssignedUnitShiftsList,
        );
        _cfsMarkers = _cfsMarkers.concat(newCfsMarkers);
      });

      setAllAssignedUnitShifts(_assignedUnitShiftsList);
      setAssignedUnitShifts(_assignedUnitShifts);
      setActiveCfsMarkers(_cfsMarkers);
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    }
  };

  // Type-safe function to handle CFS filter state changes
  const handleFilterChange = (newFilter: Partial<CFSFilterState>) => {
    setCfsFilterState((prev) => ({
      ...prev,
      ...newFilter,
    }));
  };

  // Memoize matching CFS lookup for better performance
  const cfsLookup = useMemo(() => {
    return cfsList.reduce((lookup, cfs) => {
      if (cfs._id) {
        lookup[cfs._id] = cfs;
      }
      return lookup;
    }, {} as Record<string, ICFS>);
  }, [cfsList]);

  // Memoize agency IDs of matching types for better filter performance
  const agencyIdsOfMatchingTypes = useMemo(() => {
    if (!cfsFilterState.agencyType?.length) return [];

    return agencies
      .filter((agency) => cfsFilterState.agencyType?.includes(agency.type))
      .map((agency) => agency._id);
  }, [agencies, cfsFilterState.agencyType]);

  const filteredCfsMarkers = useMemo(() => {
    return activeCfsMarkers.filter((marker) => {
      const matchingCfs = cfsLookup[marker.key as string];
      if (!matchingCfs) return true;

      // Search text filter
      if (cfsFilterState.searchText) {
        const currentSearchText = cfsFilterState.searchText.toLowerCase();
        if (!marker.title?.toLowerCase().includes(currentSearchText)) {
          return false;
        }
      }

      // Severity filter
      if (
        cfsFilterState.severity &&
        matchingCfs.severity !== cfsFilterState.severity
      ) {
        return false;
      }

      // Assignment status filter
      if (cfsFilterState.assignmentStatus) {
        if (cfsFilterState.assignmentStatus === 'isReadyToDispatch') {
          if (
            matchingCfs.assignmentStatus !== AssignmentStatus.NEW ||
            !matchingCfs.isReadyToDispatch
          ) {
            return false;
          }
        } else if (
          matchingCfs.assignmentStatus !== cfsFilterState.assignmentStatus
        ) {
          return false;
        }
      }

      // Read by filter
      if (cfsFilterState.readBy) {
        return false;
      }

      // Agency filter
      if (cfsFilterState.agency) {
        return matchingCfs.assignedAgencies?.includes(cfsFilterState.agency);
      }

      // Agency Type filter
      if (cfsFilterState.agencyType?.length) {
        return matchingCfs.assignedAgencies?.some((id) =>
          agencyIdsOfMatchingTypes.includes(id),
        );
      }

      return true;
    });
  }, [activeCfsMarkers, cfsLookup, cfsFilterState, agencyIdsOfMatchingTypes]);

  return (
    <UnitShiftContext.Provider
      value={{
        onDutyUnitShifts,
        waitingUnitShifts,
        scheduledUnitShifts,
        assignedUnitShifts,
        unitShiftMarkers,
        filteredUnitShiftMarkers,
        activeCfsMarkers,
        unitShiftAddresses,
        filterValues,
        searchText,
        setFilterValues,
        setSearchText,
        fetchScheduledUnitShifts,
        fetchOnDutyUnitShifts,
        fetchWaitingUnitShifts,
        fetchAssignedUnitShifts,
        fetchAllActiveUnitShifts,
        cfsSearchText,
        setCfsSearchText,
        cfsFilterState,
        setCfsFilterState: handleFilterChange,
        filteredCfsMarkers,
        allFetchedUnitsMap,
        setBackRoute: handleSetBackRoute,
        clearBackRoute,
        backRoute,
      }}
    >
      {children}
    </UnitShiftContext.Provider>
  );
};
